import { useCallback, useEffect, useRef, useState } from "react";

export function useHarvestEntryUndoRedo({ rowsRef, setRows }) {
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);

  useEffect(() => {
    undoStackRef.current = undoStack;
  }, [undoStack]);
  useEffect(() => {
    redoStackRef.current = redoStack;
  }, [redoStack]);

  // Prevent history writes while applying undo/redo
  const isApplyingHistoryRef = useRef(false);

  // Coalesce rapid edits into a single undo step
  const historySessionRef = useRef({ active: false, timer: null });

  const resetHistorySession = useCallback(() => {
    historySessionRef.current.active = false;
    if (historySessionRef.current.timer) {
      clearTimeout(historySessionRef.current.timer);
      historySessionRef.current.timer = null;
    }
  }, []);

  // Compare only fields that change during editing (so we can dedupe/no-op)
  const normalize = (v) => (v === undefined ? null : v);
  const areRowsEquivalentForHistory = useCallback((a, b) => {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;

    const mapB = new Map();
    for (const r of b) mapB.set(r.id, r);

    for (const ra of a) {
      const rb = mapB.get(ra.id);
      if (!rb) return false;

      if (normalize(ra.pendingHarvestDate) !== normalize(rb.pendingHarvestDate))
        return false;
      if (normalize(ra.pendingYield) !== normalize(rb.pendingYield))
        return false;
      if (normalize(ra.pendingWeight) !== normalize(rb.pendingWeight))
        return false;
      if (normalize(ra.pendingUnharvest) !== normalize(rb.pendingUnharvest))
        return false;
      if (normalize(ra.pendingTubWeight) !== normalize(rb.pendingTubWeight))
        return false;
      if (JSON.stringify(normalize(ra.pendingPloughedReasons)) !== JSON.stringify(normalize(rb.pendingPloughedReasons)))
        return false;
      if (normalize(ra.pendingPloughedNote) !== normalize(rb.pendingPloughedNote))
        return false;
    }

    return true;
  }, []);

  const pushUndoSnapshot = useCallback(
    (prevRowsSnapshot) => {
      if (isApplyingHistoryRef.current) return;

      setUndoStack((u) => {
        const last = u[u.length - 1];
        if (last && areRowsEquivalentForHistory(last, prevRowsSnapshot)) {
          return u; // dedupe identical snapshots
        }

        const next = [...u, prevRowsSnapshot];
        // Keep history bounded
        const MAX = 150;
        const trimmed =
          next.length > MAX ? next.slice(next.length - MAX) : next;
        undoStackRef.current = trimmed;
        return trimmed;
      });

      // Any new edit invalidates redo
      setRedoStack(() => {
        redoStackRef.current = [];
        return [];
      });
    },
    [areRowsEquivalentForHistory],
  );

  const undo = useCallback(() => {
    const u = undoStackRef.current;
    if (!u.length) return;

    resetHistorySession();
    isApplyingHistoryRef.current = true;

    const current = rowsRef.current;
    const last = u[u.length - 1];
    const nextUndo = u.slice(0, -1);
    const nextRedo = [...redoStackRef.current, current];

    undoStackRef.current = nextUndo;
    redoStackRef.current = nextRedo;

    setUndoStack(nextUndo);
    setRedoStack(nextRedo);
    setRows(last);

    // Release guard after state applies
    requestAnimationFrame(() => {
      isApplyingHistoryRef.current = false;
    });
  }, [resetHistorySession, rowsRef, setRows]);

  const redo = useCallback(() => {
    const r = redoStackRef.current;
    if (!r.length) return;

    resetHistorySession();
    isApplyingHistoryRef.current = true;

    const current = rowsRef.current;
    const last = r[r.length - 1];
    const nextRedo = r.slice(0, -1);
    const nextUndo = [...undoStackRef.current, current];

    redoStackRef.current = nextRedo;
    undoStackRef.current = nextUndo;

    setRedoStack(nextRedo);
    setUndoStack(nextUndo);
    setRows(last);

    requestAnimationFrame(() => {
      isApplyingHistoryRef.current = false;
    });
  }, [resetHistorySession, rowsRef, setRows]);

  const resetHistory = useCallback(() => {
    resetHistorySession();
    setUndoStack([]);
    setRedoStack([]);
    undoStackRef.current = [];
    redoStackRef.current = [];
  }, [resetHistorySession]);

  return {
    undoStack,
    redoStack,
    undo,
    redo,
    pushUndoSnapshot,
    areRowsEquivalentForHistory,
    isApplyingHistoryRef,
    historySessionRef,
    resetHistory,
  };
}
